/// <amd-module name="Core/Medius.Core.Web/Scripts/components/taskHandling/shared/modals/rejectManagementWizard/rejectManagementWizard"/>
import * as React from "react";
import { Button, ButtonGroup, DialogPart, FlexLayout, FlexSpacer, InstantSearchBox, LocalizationContext, ModalDialogShell, StringMap, Text, UploadProgressEnum } from "@medius/ui-controls";
import { translate } from "Core/Medius.Core.Web/Scripts/lib/globalization";
import RejectManagementWizardGrid from "./rejectManagementWizardGrid";
import { postISupplierInvoiceRejectionDto, getSelectedReasonCodes, getEmailEligibility } from "./services";
import { postFormData } from "Core/Medius.Core.Web/Scripts/Medius/core/fetch/rest";
import { IRecipientAddress, IRejectManagementReasonCode, ISupplierInvoiceRejectionDto, IUploadedAttachment, RejectedEntityType, RejectionType, RejectManagementWizardType } from "./interfaces";
import { handleError } from "Core/Medius.Core.Web/Scripts/lib/errorHandling/errorHandler";
import { RejectManagementEmailWizard } from "./rejectManagementEmailWizard";
import { useInvoiceReasonCodeData } from "./useInvoiceReasonCodeData";
import { mapFromNorthstarToApa } from "Core/Medius.Core.Web/Scripts/lib/northstarLocalization";
import { getCultureCode } from "Core/Medius.Core.Web/Scripts/Medius/lib/utils/user";
import { useRejectManagementEmailForm } from "./useRejectManagementEmailForm";
import { FieldValues } from "react-hook-form";
import { ReclassifyTypeModal } from "./reclassifyTypeModal";
import { RiskFactorReasonCode, RiskFactorsForInvoice } from "Core/Medius.Core.Web/Scripts/Medius/apps/task/RiskFactor/RiskFactorComponentDtos/riskFactorsForInvoiceDto";
import { saveRiskDetails } from "../../../fraudPopup/service";
import { isEmailSendingInRejectionManagementEnabled } from "Core/Medius.Core.Web/Scripts/Medius/core/featureToggle";
import { IAttachmentItem } from "./emailAttachments/interfaces";
const rest = require("Core/Medius.Core.Web/Scripts/Medius/core/communication/json/rest");

export interface IRejectWizardCloseEvent {
    wasConfirmed?: boolean, 
    targetDocumentType?: string, 
    wasEmailSent?: boolean,
    riskFactorReasonCode?: string;
}

export interface RejectManagementWizardProps {
    wizardType: RejectManagementWizardType;
    documentId: number;
    hierarchyId: string;
    task: string;
    rejectedEntityType: RejectedEntityType;
    onClose: (closeEventDetails: IRejectWizardCloseEvent) => void;
    possibleReclassifyTypesList?: string[];
    currentDocumentType?: string;
    isFraudAndRiskProtectionEnabled?: boolean;
    riskDetails?: RiskFactorsForInvoice;
    taskId?: number;
}

enum RejectManagementWizardStep {
    ReasonCode,
    Email,
    ReclassifyType
}

export const RejectManagementWizard: React.FC<RejectManagementWizardProps> = ({
    wizardType,
    documentId,
    hierarchyId,
    task,
    rejectedEntityType,
    onClose,
    possibleReclassifyTypesList,
    currentDocumentType,
    isFraudAndRiskProtectionEnabled,
    riskDetails,
    taskId
}) => {
    const searchLabelId = "searchLabelId";
    const [selection, setSelection] = React.useState<IRejectManagementReasonCode[]>([]);
    const [step, setStep] = React.useState<RejectManagementWizardStep>(RejectManagementWizardStep.ReasonCode);
    const [targetDocumentType, setTargetDocumentType] = React.useState<string>(null);
    const [isEligibleForEmail, setIsEligibleForEmail] = React.useState<boolean | null>(null);
    const formContext = useRejectManagementEmailForm();
    const { handleSubmit, watch } = formContext;
    const isSelectionMandatory = selection?.length === 0;

    const getWizardTitle = React.useCallback((): string => {
        switch (wizardType) {
            case RejectManagementWizardType.Delete:
                return translate("#PurchaseToPay/documentReasonCodes/deleteInvoice");
            case RejectManagementWizardType.Invalidate:
                return translate("#PurchaseToPay/documentReasonCodes/invalidateInvoice");
            case RejectManagementWizardType.InvalidateAndEdit:
                return translate("#PurchaseToPay/documentReasonCodes/invalidateAndEditInvoice");
        }
    }, [wizardType]);

    const hasExternalType = selection.some(rc => Array.isArray(rc.types) && rc.types.includes("External"));

    const getButtonLabel = React.useCallback((): string => {
        if (step === RejectManagementWizardStep.Email) {
            switch (wizardType) {
                case RejectManagementWizardType.Delete:
                    return translate("#Core/deleteIt");
                case RejectManagementWizardType.Invalidate:
                    return translate("#Core/invalidateIt");
                case RejectManagementWizardType.InvalidateAndEdit:
                    return translate("#Core/next");
            }
        }
        if (step === RejectManagementWizardStep.ReclassifyType) {
            return translate("#Core/registerDocument");
        }

        if (hasExternalType && isEmailSendingInRejectionManagementEnabled() && isEligibleForEmail === true) 
            return translate("#Core/next");

        switch (wizardType) {
            case RejectManagementWizardType.Delete:
                return translate("#Core/deleteIt");
            case RejectManagementWizardType.Invalidate:
                return translate("#Core/invalidateIt");
            case RejectManagementWizardType.InvalidateAndEdit:
                return translate("#Core/next");
        }
    }, [wizardType, hasExternalType, step, isEligibleForEmail]);

    const getModalTitle = React.useCallback((): string => {
        if (step === RejectManagementWizardStep.Email) {
            return translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/sendEmailToSupplier");
        }
        if (step === RejectManagementWizardStep.ReclassifyType) {
            return translate("#Core/chooseReclassifyDocumentType");
        }
        return translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/selectReasonCode");
    }, [step]);

    const invoiceReasonCodeData = useInvoiceReasonCodeData(wizardType, hierarchyId);
    const { searching: { changeSearchTerm: setSearch } } = invoiceReasonCodeData;

    const getTranslatedDocumentType = (documentType: string): string => {
        switch (documentType) {
            case "Medius.Core.Entities.Integration.EDIDocumentImport":
                return translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/documentType/eDIDocumentImport");
            case "Medius.ExpenseInvoice.Entities.ExpenseInvoice":
                return translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/documentType/expenseInvoice");
            case "Medius.OrderbasedInvoice.Entities.OrderbasedInvoice":
                return translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/documentType/orderbasedInvoice");
            case "Medius.Apa.Persistence.PrepaymentInvoice.PrepaymentSupplierInvoice":
                return translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/documentType/prepaymentSupplierInvoice");
            case "Medius.ContractbasedInvoice.Entities.ContractbasedInvoice":
                return translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/documentType/recurringInvoice");
            default:
                return documentType;
        }
    };

    const executeRejectionFlow = async (formData: FieldValues) => {
        try {
            const sendEmailInformation = shouldSendEmailInformation(formData);

            let uploadedAttachments: IUploadedAttachment[] = [];
            if (sendEmailInformation) {
                uploadedAttachments = await includeEmailAttachments(formData) ?? [];
            }  
            
            const supplierInvoiceRejectionDto: ISupplierInvoiceRejectionDto = {
                documentId: documentId,
                documentType: getTranslatedDocumentType(currentDocumentType),
                operation: wizardType === RejectManagementWizardType.Delete ? translate("#Core/delete") : translate("#Enterprise/invalidate"),
                task: task,
                rejectedEntityType: rejectedEntityType,
                rejectionType: wizardType === RejectManagementWizardType.Delete 
                    ? RejectionType.Delete 
                    : RejectionType.Invalidate,
                invoiceRejectionReasonItems: selection.map(rc => ({
                    reasonCodeId: rc.id,
                    actionId: rc.rejectionActionId
                })),
                hasExternalReasonCode: hasExternalType,
                sendToSupplier: sendEmailInformation,
                ...(sendEmailInformation && {
                    invoiceRejectionEmail: {
                        emailTo: formData.emailTo.filter((ra: IRecipientAddress) => ra.value.trim() !== ""),
                        carbonCopy: formData.carbonCopy.filter((ra: IRecipientAddress) => ra.value.trim() !== ""),
                        sendCopy: formData.sendCopy,
                        messageSubject: formData.messageSubject,
                        content: formData.content,
                        attachments: uploadedAttachments
                    }
                })
            };

            await postISupplierInvoiceRejectionDto(supplierInvoiceRejectionDto);

            if (isFraudAndRiskProtectionEnabled) {
                const riskDetailsDto = getRiskDetails(selection);
                await saveRiskDetails(taskId, riskDetailsDto);
            }

            const actualEmailSent = sendEmailInformation && isEligibleForEmail;
            onClose({wasConfirmed: true, targetDocumentType, wasEmailSent: actualEmailSent, riskFactorReasonCode: getRiskFactorReasonCode(selection)}); // confirmed
        } catch (e) {
            handleError(e);
        }
    };

    const shouldSendEmailInformation = (formData: FieldValues): boolean => {
        if (isEligibleForEmail === false) {
            return false;
        }
        if (step === RejectManagementWizardStep.Email) {
            return formData.sendToSupplier;
        }
        if (step === RejectManagementWizardStep.ReclassifyType && hasExternalType && isEmailSendingInRejectionManagementEnabled()) {
            return formData.sendToSupplier;
        }
        return false;
    };

    const includeEmailAttachments = async (formData: FieldValues) : Promise<IUploadedAttachment[] | null> => {
        const includedAttachments: IUploadedAttachment[] = [];
        
        // Include DocumentImage.pdf
        try {
            const documentImages = await new Promise<{Name: string, Hash: string}[]>((resolve, reject) => {
                rest.get("remedium-api/HashFilesService",
                    "GetImageHashFilesWithNames",
                    {
                        entityId: documentId,
                        entityType: currentDocumentType
                    })
                    .done((f: any) => resolve(f ?? []))
                    .fail((err: any) => reject(err));
            });

            if (Array.isArray(documentImages) && documentImages.length > 0) {
                const firstImage = documentImages[0];
                includedAttachments.push({ 
                    hash: firstImage.Hash, 
                    name: firstImage.Name,
                    isDocumentImage: true
                });
            }
        } catch (error) {
            console.warn("Could not load document image:", error);
        }

        // Include user-uploaded attachments
        if (formData.attachments?.length > 0) {
            for (const att of formData.attachments) {
                const uploadFormData = new FormData();
                uploadFormData.append("description", "Email attachment");
                uploadFormData.append("entityViewId", documentId.toString());
                uploadFormData.append("entityType", currentDocumentType);
                uploadFormData.append("isMainAttachment", "false");
                uploadFormData.append("isExternal", "false");
                uploadFormData.append("file", att.file);
                
                const result = await postFormData('Attachments/UploadFile', uploadFormData);
                includedAttachments.push({ 
                    hash: result[0]?.Hash || result.hash, 
                    name: att.file.name,
                    isDocumentImage: false
                });
            }
        }

        return includedAttachments.length > 0 ? includedAttachments : null;
    };

    const getPrioritizedSystemReasonCode = (selection: IRejectManagementReasonCode[]) => {
        const systemReasonCodeOrder = ["FraudAttempt", "DuplicateInvoice", "NotAnInvoice", "Other"];
        const sorted = [...selection].sort((a, b) => {
            const aIndex = systemReasonCodeOrder.indexOf(a.systemReasonCode);
            const bIndex = systemReasonCodeOrder.indexOf(b.systemReasonCode);
            const safeA = aIndex === -1 ? systemReasonCodeOrder.length : aIndex;
            const safeB = bIndex === -1 ? systemReasonCodeOrder.length : bIndex;
            return safeA - safeB;
        });
        const prioritizedSystemReasonCode = sorted.length > 0 ? sorted[0] : null;

        return prioritizedSystemReasonCode?.systemReasonCode;
    };

    const getRiskFactorReasonCode = (selection: IRejectManagementReasonCode[]): RiskFactorReasonCode | null => {
        if(selection === null || selection.length === 0){
            return null;
        }

        const systemReasonCode = getPrioritizedSystemReasonCode(selection);
        switch (systemReasonCode) {
            case "FraudAttempt":
                return RiskFactorReasonCode.InvoiceDeletedFraudAttempt;
            case "DuplicateInvoice":
                return RiskFactorReasonCode.InvoiceDeletedDuplicateInvoice;
            case "NotAnInvoice":
                return RiskFactorReasonCode.InvoiceDeletedNotAnInvoice;
            case "Other":
                return RiskFactorReasonCode.InvoiceDeletedOther;
            default:
                return null;
        }
    };

    const getRiskDetails = (selection: IRejectManagementReasonCode[]) => {
        const reason = getRiskFactorReasonCode(selection);
        if(reason == null){
            throw new Error("No valid system reason code found");
        }

        riskDetails.riskFactors = riskDetails.riskFactors.map(rf => ({
            ...rf,
            handledInfo: {
                ...rf.handledInfo,
                reasonCode: reason,
            }
        }));

        const riskDetailsDto = riskDetails.riskFactors.map(rf => (
            {
                RiskFactorCode: rf.code,
                HandlingReason: reason
            }
        ));
        return riskDetailsDto;
    };

    const handleDelete = handleSubmit(async (formData: FieldValues) => {

        if (step === RejectManagementWizardStep.ReclassifyType) {
            await executeRejectionFlow(formData);
            return;
        }

        if (step === RejectManagementWizardStep.Email) {
            if (wizardType === RejectManagementWizardType.InvalidateAndEdit)
                setStep(RejectManagementWizardStep.ReclassifyType);
            else
                await executeRejectionFlow(formData);
            return;
        }

        if (hasExternalType && isEmailSendingInRejectionManagementEnabled()) {
            if (isEligibleForEmail === null) {
                if (isEligibleForEmail) {
                    setStep(RejectManagementWizardStep.Email);
                    return;
                }
            } else if (isEligibleForEmail === true) {
                setStep(RejectManagementWizardStep.Email);
                return;
            }
        }

        if (wizardType === RejectManagementWizardType.InvalidateAndEdit) {
            setStep(RejectManagementWizardStep.ReclassifyType);
            return;
        }

        await executeRejectionFlow(formData);
    });

    const calculateBackState = (): RejectManagementWizardStep => {
        if (step === RejectManagementWizardStep.ReclassifyType) {
            if (hasExternalType && isEmailSendingInRejectionManagementEnabled() && isEligibleForEmail === true)
                return RejectManagementWizardStep.Email;
        }
        return RejectManagementWizardStep.ReasonCode;
    };

    React.useEffect(() => {
         let isCancelled = false;

        // initiating reason codes and email communication eligibility
        (async () => {
            try {
                const [selectedReasonCodes, eligibleForEmailCommunication] = await Promise.all([
                    getSelectedReasonCodes(documentId),
                    getEmailEligibility(documentId),
                ]);

                if (isCancelled) return;

                setSelection(selectedReasonCodes);
                setIsEligibleForEmail(eligibleForEmailCommunication?.isEligible ?? false);
            } catch (e) {
                if (!isCancelled) {
                    setIsEligibleForEmail(false);
                }
            }
        })();

        return () => {
            isCancelled = true;
        };
    },[documentId]);

    React.useEffect(() => {
        if (currentDocumentType)
            setTargetDocumentType(currentDocumentType);
    }, [currentDocumentType]);

    const attachments: IAttachmentItem[] = watch("attachments");
    const sendEmailToSupplier: boolean = watch("sendToSupplier");
    const hasFormErrors = sendEmailToSupplier && attachments.some(att => att.status === UploadProgressEnum.Failed);

    return (
        <LocalizationContext.Provider
            value={{
                translate: (key: string, _, defaultValue?: string, args?: StringMap) =>
                    mapFromNorthstarToApa(key, defaultValue, args),
                getLocale: () => getCultureCode()
            }}
        >
            <ModalDialogShell
                isOpen
                hasHeader
                height="content"
                title={getWizardTitle()}
                data-testid="reject-management-wizard"
                onRequestClose={() => onClose({wasConfirmed: false})} >
                {dialogProps => <>
                    <DialogPart.Title {...dialogProps.headerProps} />
                    <DialogPart.Content width="large">
                        <div className="reject-management-wizard">
                            <div className="reject-management-wizard__header">
                                <Text id={searchLabelId} variant="heading3">{getModalTitle()}</Text>
                                {step === RejectManagementWizardStep.ReasonCode && (
                                    <InstantSearchBox
                                        data-testid="reject-management-wizard-search-box"
                                        labelId={searchLabelId}
                                        width="fill"
                                        spacerMode="on"
                                        executeSearch={(searchTerm) => { setSearch(searchTerm); }} />
                                )}
                            </div>
                            <div className="reject-management-wizard__content">
                                {step === RejectManagementWizardStep.Email && hasExternalType && isEligibleForEmail === true && (
                                    <RejectManagementEmailWizard
                                        formContext={formContext}
                                        documentType={currentDocumentType}
                                        documentId={documentId} />)}
                                {step === RejectManagementWizardStep.ReasonCode && (
                                    <RejectManagementWizardGrid
                                        selection={selection}
                                        setSelection={setSelection}
                                        invoiceReasonCodeData={invoiceReasonCodeData}
                                    />
                                )}
                                {step === RejectManagementWizardStep.ReclassifyType && (
                                    <ReclassifyTypeModal
                                        possibleReclassifyTypeList={possibleReclassifyTypesList}
                                        targetDocumentType={targetDocumentType}
                                        setTargetDocumentType={setTargetDocumentType}
                                    />
                                )}
                            </div>
                        </div>
                    </DialogPart.Content>
                    <DialogPart.Footer>
                        <FlexLayout>
                            {(step !== RejectManagementWizardStep.ReasonCode) && (
                                <Button
                                    data-testid="reject-wizard-back-button"
                                    label={translate("#Core/back")}
                                    onClick={() =>
                                        setStep(calculateBackState())
                                    }
                                    variant="inline"
                                />
                            )}
                            <FlexSpacer />
                            <ButtonGroup>
                                <Button
                                    data-testid="reject-wizard-cancel-button"
                                    label={translate("#Core/cancel")}
                                    variant="secondary"
                                    onClick={() => onClose({wasConfirmed: false})} />
                                <Button
                                    data-testid="reject-wizard-delete-it-button"
                                    label={getButtonLabel()}
                                    onClick={handleDelete}
                                    disabled={isSelectionMandatory || hasFormErrors}
                                />
                            </ButtonGroup>
                        </FlexLayout>
                    </DialogPart.Footer>
                </>}
            </ModalDialogShell>
        </LocalizationContext.Provider>
    );
};